import { ErrorHandler, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DprStatusTrackerAccumulation,
  DprStatusTrackerFormulaContext,
  DprTableOfColumnsAccumulation,
  DprTableOfColumnsFormulaContext,
  FormulaExpression,
} from '@dpr-details/models';
import {
  buildPreviousDpr,
  buildStatusTrackerFormulaHistory,
  buildTableOfColumnsFormulaHistory,
  computeStatusTrackerAccumulation,
  computeTableOfColumnsAccumulation,
  getSectionsWithoutPreviousValues,
  getStatusTrackerHistoryColumnIds,
  getTableOfColumnsHistoryItemIds,
  isStatusTrackerColumnDerived,
  isTableOfColumnItemDerived,
} from '@dpr-details/utils';
import { ProjectSetup } from '@shared/models';
import { DprDetails } from '@shared/models/dpr';
import { ToastNotificationService } from '@shared/services';
import { DprFormulaCalculationService } from '../formula';

/**
 * Holds the derived values of the DPR currently open, for the Table of Columns and
 * Status Tracker tabs to read while they build their grids: its carry-over values
 * and what its formulas need to be recalculated as it is edited.
 *
 * The values are derived rather than stored: they are worked out on every load,
 * from the previous values the DPR carries, and never written into the DPR held in
 * the offline store. Offline, `DprAccumulationOfflineService` keeps those previous
 * values in step with the edits made on the device to the DPRs before it.
 */
@Injectable()
export class DprAccumulationService {
  private tableOfColumnsAccumulations = new Map<
    number,
    DprTableOfColumnsAccumulation
  >();
  private statusTrackerAccumulations = new Map<
    number,
    DprStatusTrackerAccumulation
  >();
  private tableOfColumnsFormulaContexts = new Map<
    number,
    DprTableOfColumnsFormulaContext
  >();
  private statusTrackerFormulaContexts = new Map<
    number,
    DprStatusTrackerFormulaContext
  >();
  private tableOfColumnsStoredItemIds = new Map<number, Set<number>>();
  private statusTrackerStoredColumnIds = new Map<number, Set<number>>();

  constructor(
    private dprFormulaCalculationService: DprFormulaCalculationService,
    private errorHandler: ErrorHandler,
    private toastNotificationService: ToastNotificationService
  ) {}

  /**
   * Resolves the derived values of the given DPR, once `reset` has dropped those of
   * the DPR before. Has to complete before the DPR reaches the offline store,
   * because that is what makes the tabs build their grids.
   */
  load(dprDetails: DprDetails, projectSetup: ProjectSetup): Observable<void> {
    if (!this.hasDerivedColumns(projectSetup)) {
      return of(undefined);
    }

    return of(dprDetails).pipe(
      map(() => {
        const previousDpr = buildPreviousDpr(
          dprDetails,
          projectSetup.tableOfColumns
        );
        const tableOfColumnsAccumulations = computeTableOfColumnsAccumulation(
          projectSetup.tableOfColumns,
          previousDpr
        );
        const statusTrackerAccumulations = computeStatusTrackerAccumulation(
          projectSetup.statusTrackers,
          previousDpr
        );
        const tableOfColumnsFormulaContexts =
          this.dprFormulaCalculationService.createTableOfColumnsContexts(
            projectSetup.tableOfColumns,
            dprDetails.dprDateTime,
            previousDpr ? buildTableOfColumnsFormulaHistory(previousDpr) : []
          );
        const statusTrackerFormulaContexts =
          this.dprFormulaCalculationService.createStatusTrackerContexts(
            projectSetup.statusTrackers,
            dprDetails.dprDateTime,
            previousDpr ? buildStatusTrackerFormulaHistory(previousDpr) : []
          );

        const { statusTrackerStoredColumnIds, tableOfColumnsStoredItemIds } =
          this.getStoredColumnIds(dprDetails, projectSetup);

        this.keepStoredValues(
          statusTrackerStoredColumnIds,
          statusTrackerAccumulations,
          statusTrackerFormulaContexts
        );
        this.keepStoredValues(
          tableOfColumnsStoredItemIds,
          tableOfColumnsAccumulations,
          tableOfColumnsFormulaContexts
        );

        // Assigned only once everything has resolved, so the tabs never mix
        // computed values with stored ones.
        this.tableOfColumnsAccumulations = tableOfColumnsAccumulations;
        this.statusTrackerAccumulations = statusTrackerAccumulations;
        this.tableOfColumnsFormulaContexts = tableOfColumnsFormulaContexts;
        this.statusTrackerFormulaContexts = statusTrackerFormulaContexts;
        this.tableOfColumnsStoredItemIds = tableOfColumnsStoredItemIds;
        this.statusTrackerStoredColumnIds = statusTrackerStoredColumnIds;

        return undefined;
      }),
      // If the values cannot be worked out, the tabs build their grids without
      // them rather than failing the whole DPR load.
      catchError((error) => {
        this.errorHandler.handleError(error);
        this.toastNotificationService.warn(
          $localize`:@@dprDetails.dprAccumulationService.txtCalculationFailed:Carry-over and formula values couldn't be calculated for this DPR, so those columns may be incomplete.`
        );

        return of(undefined);
      })
    );
  }

  getTableOfColumnsAccumulation(
    tableOfColumnId: number
  ): DprTableOfColumnsAccumulation | undefined {
    return this.tableOfColumnsAccumulations.get(+tableOfColumnId);
  }

  getStatusTrackerAccumulations(): Map<number, DprStatusTrackerAccumulation> {
    return this.statusTrackerAccumulations;
  }

  getTableOfColumnsFormulaContext(
    tableOfColumnId: number
  ): DprTableOfColumnsFormulaContext | undefined {
    return this.tableOfColumnsFormulaContexts.get(+tableOfColumnId);
  }

  getStatusTrackerFormulaContexts(): Map<
    number,
    DprStatusTrackerFormulaContext
  > {
    return this.statusTrackerFormulaContexts;
  }

  getStatusTrackerFormulaContext(
    statusTrackerId: number
  ): DprStatusTrackerFormulaContext | undefined {
    return this.getStatusTrackerFormulaContexts().get(+statusTrackerId);
  }

  /** The derived items of a table that show the value stored on the DPR. */
  getTableOfColumnsStoredItemIds(
    tableOfColumnId: number
  ): Set<number> | undefined {
    return this.tableOfColumnsStoredItemIds.get(+tableOfColumnId);
  }

  /** The derived columns, by status tracker id, that show the value stored on the DPR. */
  getStatusTrackerStoredColumnIds(): Map<number, Set<number>> {
    return this.statusTrackerStoredColumnIds;
  }

  /**
   * Drops the values of the DPR that was open. Call it as soon as the next DPR
   * starts loading: a tab can rebuild its grid from the DPR already in the offline
   * store before `load` resolves, and would otherwise show the values of the DPR
   * before it.
   */
  reset(): void {
    this.tableOfColumnsAccumulations = new Map();
    this.statusTrackerAccumulations = new Map();
    this.tableOfColumnsFormulaContexts = new Map();
    this.statusTrackerFormulaContexts = new Map();
    this.tableOfColumnsStoredItemIds = new Map();
    this.statusTrackerStoredColumnIds = new Map();
  }

  //#region Private methods

  /**
   * The derived columns that show the values stored on the DPR, by section. Worked
   * out without its previous values, a section would show initial values and restart
   * its running totals, which look right but aren't. Every other formula is still
   * worked out, and recalculated as the DPR is edited.
   */
  private getStoredColumnIds(
    dprDetails: DprDetails,
    projectSetup: ProjectSetup
  ): {
    statusTrackerStoredColumnIds: Map<number, Set<number>>;
    tableOfColumnsStoredItemIds: Map<number, Set<number>>;
  } {
    const sectionsWithoutHistory = getSectionsWithoutPreviousValues(
      dprDetails,
      projectSetup.statusTrackers,
      projectSetup.tableOfColumns
    );

    if (
      sectionsWithoutHistory.includesStatusTracker ||
      sectionsWithoutHistory.tableOfColumnIds.size
    ) {
      console.warn(
        `DPR ${dprDetails?.id} came without the previous values some of its ` +
          'carry-overs and formulas are worked out from, so those show the values stored on it.'
      );
    }

    return {
      statusTrackerStoredColumnIds: new Map(
        sectionsWithoutHistory.includesStatusTracker
          ? (projectSetup.statusTrackers ?? []).map((statusTracker) => [
              +statusTracker.id,
              getStatusTrackerHistoryColumnIds(statusTracker),
            ])
          : []
      ),
      tableOfColumnsStoredItemIds: new Map(
        (projectSetup.tableOfColumns ?? [])
          .filter((tableOfColumn) =>
            sectionsWithoutHistory.tableOfColumnIds.has(+tableOfColumn.id)
          )
          .map((tableOfColumn) => [
            +tableOfColumn.id,
            getTableOfColumnsHistoryItemIds(tableOfColumn),
          ])
      ),
    };
  }

  /**
   * A section's carry-overs are left out and its context keeps only the formulas
   * that don't read the DPR before. Those never read one that does, so they still
   * work out the same.
   */
  private keepStoredValues<TContext extends { formulas: FormulaExpression[] }>(
    storedColumnIdsBySectionId: Map<number, Set<number>>,
    accumulations: Map<number, unknown>,
    formulaContexts: Map<number, TContext>
  ): void {
    storedColumnIdsBySectionId.forEach((storedColumnIds, sectionId) => {
      accumulations.delete(sectionId);

      const context = formulaContexts.get(sectionId);
      const formulas = context?.formulas.filter(
        (formula) => !storedColumnIds.has(formula.columnId)
      );

      if (formulas?.length) {
        formulaContexts.set(sectionId, { ...context, formulas });
      } else {
        formulaContexts.delete(sectionId);
      }
    });
  }

  private hasDerivedColumns(projectSetup: ProjectSetup): boolean {
    return (
      !!projectSetup?.tableOfColumns?.some((tableOfColumn) =>
        tableOfColumn.tableOfColumnItems?.some((item) =>
          isTableOfColumnItemDerived(item)
        )
      ) ||
      !!projectSetup?.statusTrackers?.some((statusTracker) =>
        statusTracker.columns?.some((column) =>
          isStatusTrackerColumnDerived(statusTracker, column)
        )
      )
    );
  }

  //#endregion
}
